import { useQuery } from '@apollo/client'
import {
  allPass,
  ascend,
  chain,
  compose,
  filter,
  identity,
  map,
  pluck,
  reduce,
  sort,
  uniq,
} from 'ramda'
import React, { useContext, useMemo } from 'react'

import { GetOrderProductsDocument, GetOrderProductsQuery } from '../../../../graphql/magento'
import useLogAndCaptureError from '../../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../../layouts/context'
import {
  isMagentoProductsQueryConfiguredSimpleProduct,
  MagentoProductsQueryItem,
} from '../../../../lib/products'
import { isNotNull } from '../../../../utils/collectionTools'
import { rawItemPrice } from '../../../../utils/priceTools'
import Spinner from '../../../Spinner'
import EmptyHistory from '../EmptyHistory'
import AutoShipStatus from './AutoShipStatus'
import { AutoShipHR } from './styled'
import {
  AutoShipPageContentProps,
  QueriedAutoShip,
  QueriedAutoShipItem,
  QueriedAutoShipItemAndSimpleProduct,
  QueriedAutoShips,
} from './types'

const isActive = (autoShip: QueriedAutoShip | null): autoShip is QueriedAutoShip =>
  !!autoShip && autoShip.status === 'Active'
const isCompleted = (autoShip: QueriedAutoShip | null): autoShip is QueriedAutoShip =>
  !!autoShip && autoShip.status === 'Completed'
const isCancelled = (autoShip: QueriedAutoShip | null): autoShip is QueriedAutoShip =>
  !!autoShip && autoShip.status === 'Cancelled'
const isSuspended = (autoShip: QueriedAutoShip | null): autoShip is QueriedAutoShip =>
  !!autoShip && autoShip.status === 'Suspended'
const isPayFail1 = (autoShip: QueriedAutoShip | null): autoShip is QueriedAutoShip =>
  !!autoShip && autoShip.status === 'Payment Failed: 1 Time'
const isPayFail2 = (autoShip: QueriedAutoShip | null): autoShip is QueriedAutoShip =>
  !!autoShip && autoShip.status === 'Payment Failed: 2 Times'
const isPayFail3 = (autoShip: QueriedAutoShip | null): autoShip is QueriedAutoShip =>
  !!autoShip && autoShip.status === 'Payment Failed: 3 Times'

const filterActive: (autoShips: QueriedAutoShips) => ReadonlyArray<QueriedAutoShip> =
  filter(isActive)
const filterCompleted: (autoShips: QueriedAutoShips) => ReadonlyArray<QueriedAutoShip> =
  filter(isCompleted)
const filterCancelled: (autoShips: QueriedAutoShips) => ReadonlyArray<QueriedAutoShip> =
  filter(isCancelled)
const filterSuspended: (autoShips: QueriedAutoShips) => ReadonlyArray<QueriedAutoShip> =
  filter(isSuspended)
const filterPayFail1: (autoShips: QueriedAutoShips) => ReadonlyArray<QueriedAutoShip> =
  filter(isPayFail1)
const filterPayFail2: (autoShips: QueriedAutoShips) => ReadonlyArray<QueriedAutoShip> =
  filter(isPayFail2)
const filterPayFail3: (autoShips: QueriedAutoShips) => ReadonlyArray<QueriedAutoShip> =
  filter(isPayFail3)

const pluckSku = pluck('sku')

const uniqueSkusFromAutoShips: (autoShips: QueriedAutoShips) => Array<string> = chain(
  compose(
    uniq,
    // sort skus for deterministic arguments
    sort<string>(ascend(identity)),
    pluckSku,
    (autoShip: QueriedAutoShip | null) => filter(isNotNull, autoShip?.items ?? []),
  ),
)

const isSimpleProductWithSku = (sku: string) =>
  allPass([
    isMagentoProductsQueryConfiguredSimpleProduct,
    (a: MagentoProductsQueryItem): a is NonNullable<MagentoProductsQueryItem> => a?.sku === sku,
  ])

const calculateAutoShipTotal: (
  itemsAndProducts: ReadonlyArray<QueriedAutoShipItemAndSimpleProduct>,
) => number = reduce((acc, [item, matchingProduct]) => {
  const matchingUnitPrice = matchingProduct.price_tiers?.[0]?.final_price?.value
  const itemAmount =
    matchingUnitPrice != null
      ? rawItemPrice({
          price: matchingUnitPrice,
          qty: item.quantity,
        })
      : 0
  return acc + itemAmount
}, 0)

const pairAutoShipItemsAndProducts: (
  productsData: GetOrderProductsQuery | undefined,
) => (autoShip: QueriedAutoShip) => Array<QueriedAutoShipItemAndSimpleProduct> = (productsData) =>
  compose(
    chain((item: QueriedAutoShipItem | null) => {
      const matchingProduct = item
        ? productsData?.products?.items?.find(isSimpleProductWithSku(item.sku))
        : undefined
      return item && matchingProduct ? [[item, matchingProduct]] : []
    }),
    (autoShip: QueriedAutoShip) => autoShip.items,
  )

const AutoShipPageContent: React.VFC<AutoShipPageContentProps> = ({ data, loading }) => {
  const skusToQuery = useMemo(
    () => uniqueSkusFromAutoShips(data?.getCustomerAutoShips?.autoShips ?? []),
    [data?.getCustomerAutoShips?.autoShips],
  )

  const token = useContext(TokenContext)
  const {
    data: productsData,
    error: productsError,
    loading: productsLoading,
  } = useQuery(GetOrderProductsDocument, {
    context: { token },
    variables: { sku: skusToQuery },
  })
  useLogAndCaptureError(productsError)

  const {
    activeAutoShips,
    completedAutoShips,
    cancelledAutoShips,
    suspendedAutoShips,
    payFail1AutoShips,
    payFail2AutoShips,
    payFail3AutoShips,
  } = useMemo(() => {
    const pairItemsAndProducts = pairAutoShipItemsAndProducts(productsData)
    const mapToAutoShipWithProps = map(
      compose(
        ({ autoShip, itemsAndProducts }) => ({
          autoShip,
          itemsAndProducts,
          total: calculateAutoShipTotal(itemsAndProducts),
        }),
        (autoShip: QueriedAutoShip) => ({
          autoShip,
          itemsAndProducts: pairItemsAndProducts(autoShip),
        }),
      ),
    )
    return {
      activeAutoShips: mapToAutoShipWithProps(
        filterActive(data?.getCustomerAutoShips?.autoShips ?? []),
      ),
      completedAutoShips: mapToAutoShipWithProps(
        filterCompleted(data?.getCustomerAutoShips?.autoShips ?? []),
      ),
      cancelledAutoShips: mapToAutoShipWithProps(
        filterCancelled(data?.getCustomerAutoShips?.autoShips ?? []),
      ),
      suspendedAutoShips: mapToAutoShipWithProps(
        filterSuspended(data?.getCustomerAutoShips?.autoShips ?? []),
      ),
      payFail1AutoShips: mapToAutoShipWithProps(
        filterPayFail1(data?.getCustomerAutoShips?.autoShips ?? []),
      ),
      payFail2AutoShips: mapToAutoShipWithProps(
        filterPayFail2(data?.getCustomerAutoShips?.autoShips ?? []),
      ),
      payFail3AutoShips: mapToAutoShipWithProps(
        filterPayFail3(data?.getCustomerAutoShips?.autoShips ?? []),
      ),
    }
  }, [data?.getCustomerAutoShips?.autoShips, productsData])

  return (
    <>
      <Spinner loading={loading} />
      {!!activeAutoShips.length && (
        <>
          <h4>Active Auto-Ships</h4>
          {activeAutoShips.map((props) => (
            <div key={props.autoShip.autoShipId}>
              <AutoShipStatus productsLoading={productsLoading} {...props} />
            </div>
          ))}
          <AutoShipHR />
        </>
      )}

      {(!!cancelledAutoShips.length ||
        !!suspendedAutoShips.length ||
        !!completedAutoShips.length ||
        !!payFail1AutoShips.length ||
        !!payFail2AutoShips.length ||
        !!payFail3AutoShips.length) && (
        <>
          <h4>Inactive Auto-Ships</h4>
          {cancelledAutoShips.map((props) => (
            <div key={props.autoShip.autoShipId}>
              <AutoShipStatus productsLoading={productsLoading} {...props} />
            </div>
          ))}
          {suspendedAutoShips.map((props) => (
            <div key={props.autoShip.autoShipId}>
              <AutoShipStatus productsLoading={productsLoading} {...props} />
            </div>
          ))}
          {completedAutoShips.map((props) => (
            <div key={props.autoShip.autoShipId}>
              <AutoShipStatus productsLoading={productsLoading} {...props} />
            </div>
          ))}
          {payFail1AutoShips.map((props) => (
            <div key={props.autoShip.autoShipId}>
              <AutoShipStatus productsLoading={productsLoading} {...props} />
            </div>
          ))}
          {payFail2AutoShips.map((props) => (
            <div key={props.autoShip.autoShipId}>
              <AutoShipStatus productsLoading={productsLoading} {...props} />
            </div>
          ))}
          {payFail3AutoShips.map((props) => (
            <div key={props.autoShip.autoShipId}>
              <AutoShipStatus productsLoading={productsLoading} {...props} />
            </div>
          ))}
        </>
      )}
      {!loading &&
        !activeAutoShips.length &&
        !completedAutoShips.length &&
        !cancelledAutoShips.length &&
        !suspendedAutoShips.length && (
          <EmptyHistory message="No Auto-Ship history at the moment." />
        )}
    </>
  )
}

export default AutoShipPageContent
